<template>
    <div class="pb-2 flex flex-col items-stretch gap-[8px]" data-testid="sms-ai-update-panel">
        <div class="flex h-[42px] items-center gap-[16px]" data-testid="sms-ai-update-row">
            <div
                class="sms-ai-update-input-shell flex h-[42px] flex-1 items-center gap-[8px] rounded-[8px] bg-white px-[10px] py-[11px]"
                data-testid="sms-ai-update-input-shell"
            >
                <icon
                    aria-hidden="true"
                    class="h-[18px] w-[18px] shrink-0 text-[#0000FF]"
                    data-testid="sms-ai-update-icon"
                    name="regular/ai-edit-spark"
                />
                <input
                    id="sms-ai-update-instruction"
                    class="h-[20px] min-w-0 flex-1 border-0 bg-transparent p-0 text-[14px] leading-[20px] text-gray-900 outline-none placeholder:text-gray-550 disabled:cursor-not-allowed disabled:text-gray-500"
                    data-testid="sms-ai-update-instruction-input"
                    maxlength="255"
                    type="text"
                    :aria-label="$t('smsCommunication.aiDraft.updateInstructionPlaceholder')"
                    :disabled="!canEditInstruction"
                    :placeholder="$t('smsCommunication.aiDraft.updateInstructionPlaceholder')"
                    :value="modelValue"
                    @input="onInput"
                    @compositionstart="onCompositionStart"
                    @compositionend="onCompositionEnd"
                    @keydown.enter="onInstructionEnter"
                />
                <ai-draft-submit-button
                    :disabled="!canSubmitUpdate"
                    :label="$t('smsCommunication.aiDraft.updateWithAi')"
                    testid-prefix="sms-ai"
                    @click="submitUpdate"
                    v-if="canEditInstruction && modelValue.trim()"
                />
            </div>

            <activix-tooltip
                data-testid="sms-ai-undo-tooltip"
                :content="$t('smsCommunication.aiDraft.undoTooltip')"
            >
                <span class="inline-flex shrink-0">
                    <button
                        :aria-label="$t('smsCommunication.aiDraft.undo')"
                        class="inline-flex h-[36px] w-[36px] items-center justify-center rounded-full bg-[#F2F2F2] p-0 text-[#595959] disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid="sms-ai-undo-button"
                        type="button"
                        :disabled="!canUseUndo"
                        @click="emit('undo')"
                    >
                        <icon
                            class="h-[16px] w-[16px]"
                            data-testid="sms-ai-undo-icon"
                            name="regular/undo"
                        />
                    </button>
                </span>
            </activix-tooltip>
        </div>

        <span
            class="text-sm text-gray-400"
            data-testid="sms-ai-service-lead-helper"
            v-if="showServiceLeadHelper"
        >
            {{ $t('smsCommunication.aiDraft.updateServiceLeadUnavailable') }}
        </span>
    </div>
</template>

<script setup lang="ts">
    import AiDraftSubmitButton from '@/components/aiDraft/AiDraftSubmitButton.vue';
    import { useImeCompositionGuard } from '@/components/sms/hooks/useImeCompositionGuard.ts';

    defineProps<{
        modelValue: string;
        canEditInstruction: boolean;
        canSubmitUpdate: boolean;
        canUseUndo: boolean;
        showServiceLeadHelper: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: string];
        submit: [];
        undo: [];
    }>();

    const { onCompositionStart, onCompositionEnd, isComposing } = useImeCompositionGuard();

    const onInput = (event: Event) => {
        emit('update:modelValue', (event.target as HTMLInputElement).value);
    };

    const onInstructionEnter = (event: KeyboardEvent) => {
        if (isComposing(event)) {
            return;
        }

        event.preventDefault();
        submitUpdate();
    };

    const submitUpdate = () => {
        emit('submit');
    };
</script>

<style scoped>
    /* Gradient ring (blue -> pink) via padding-box/border-box double-background. */
    .sms-ai-update-input-shell {
        border: 1px solid transparent;
        background: linear-gradient(#fff, #fff) padding-box,
            linear-gradient(105deg, #0038ff 0%, #0038ff 45%, #ff2d55 100%) border-box;
    }
</style>
